import { Injectable } from '@angular/core';

import {
  Axis,
  ScaleLinear,
  ScaleTime,
  Line,
  Area
} from 'd3';
import * as d3 from 'd3';
import { ScaleThreshold } from 'd3-scale';

export interface Margin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

@Injectable()
export class D3Service {

  constructor() { }

  getTimelineGenerator(xScale: ScaleTime<string, number>, yScale: ScaleLinear<number, number>): Line<any> {
    return d3.line()
      // .curve(d3.curveCardinal)
      .defined((d: any) => {
        return d.y !== null;
      })
      .x((d: any) => xScale((typeof d.x === 'string') ? new Date(d.x) : d.x))
      .y((d: any) => yScale(d.y));
  }

  getTimeAreaGenerator(xScale: ScaleTime<string, number>, yScale: ScaleLinear<number, number>, height: number): Area<any> {
    return d3.area()
      .x((d: any) => xScale((typeof d.x === 'string') ? new Date(d.x) : d.x))
      .y0(height)
      .y1((d: any) => yScale(d.y));
  }

  getFlexSVG(containerElement: HTMLElement, containerWidth: number, containerHeight: number, margin: Margin): any {
    if (containerElement.querySelector('svg')) {
      if (containerElement.querySelector('svg').querySelector('g')) {
        // Edit svg and g if they both exist in the container
        return d3.select(containerElement)
          .select('svg')
          .attr('width', containerWidth)
          .attr('height', containerHeight)
          .select('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

      } else {
        // Edit svg and append g if g is missing in the container
        return d3.select(containerElement)
          .select('svg')
          .attr('width', containerWidth)
          .attr('height', containerHeight)
          .append('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');
      }
    } else {
      // Append both svg and g if svg doesn't exist in the container
      return d3.select(containerElement)
        .append('svg')
        .attr('width', containerWidth)
        .attr('height', containerHeight)
        .append('g')
        .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');
    }
  }

  getPureWidth(element: HTMLElement): number {
    const elementPaddingLeft = parseFloat(window.getComputedStyle(element).paddingLeft);
    const elementPaddingRight = parseFloat(window.getComputedStyle(element).paddingRight);

    return (parseFloat(window.getComputedStyle(element).width) - (elementPaddingLeft + elementPaddingRight));
  }

  getPureHeight(element: HTMLElement): number {
    const elementPaddingTop = parseFloat(window.getComputedStyle(element).paddingTop);
    const elementPaddingBottom = parseFloat(window.getComputedStyle(element).paddingBottom);

    return (parseFloat(window.getComputedStyle(element).height) - (elementPaddingTop + elementPaddingBottom));
  }

  getFlexMargin(width: number, height: number, minMargin?: Margin): Margin {
    let flexMargin: Margin;

    if (minMargin) {
      flexMargin = {
        top: height / 13 + minMargin.top,
        right: width / 30 + minMargin.right,
        bottom: height / 13 + minMargin.bottom,
        left: width / 30 + minMargin.left
      };
    } else {
      flexMargin = {
        top: height / 13,
        right: width / 30,
        bottom: height / 13,
        left: width / 30
      };
    }

    return flexMargin;
  }

  getTimeScaleX(width: number, dataset?: any[], min?: Date, max?: Date, padding?: number): ScaleTime<number, number> {
    const rangePadding = padding || 0;
    let minDomain: Date;
    let maxDomain: Date;

    minDomain = min || d3.min(dataset, (d: any) => (typeof d.x === 'string') ? new Date(d.x) : d.x);
    maxDomain = max || d3.max(dataset, (d: any) => (typeof d.x === 'string') ? new Date(d.x) : d.x);

    return d3.scaleTime()
      .domain([minDomain, maxDomain])
      .range([(rangePadding), (width - rangePadding)]);
  }

  getScaleY(height: number, dataset?: any[], min?: number, max?: number, padding?: number): ScaleLinear<number, number> {
    const rangePadding = padding || 0;
    let minDomain: number;
    let maxDomain: number;

    minDomain = min != null ? min : d3.min(dataset, (d: any) => d.y);
    maxDomain = max != null ? max : d3.max(dataset, (d: any) => d.y);

    return d3.scaleLinear()
      .domain([minDomain, maxDomain])
      .range([height - rangePadding, rangePadding]);
  }

  getAxisX(xScale: any, width: number, ticks?: number, innerTickSize?: number, outerTickSize?: number,
    tickPadding?: number, tickFormat?: any): Axis<{}> {
    return d3.axisBottom(xScale)
      .tickSizeInner((innerTickSize || 0))
      .tickSizeOuter((outerTickSize || 0))
      .ticks(ticks || (width / 90))
      .tickFormat(tickFormat || this.getTimeFormat)
      .tickPadding((tickPadding || 10));
  }

  getAxisY(yScale: any, height: number, ticks?: number, innerTickSize?: number, outerTickSize?: number, tickPadding?: number): Axis<{}> {
    return d3.axisLeft(yScale)
      .tickSizeInner((innerTickSize || 0))
      .tickSizeOuter((outerTickSize || 0))
      .ticks(ticks || (height / 50))
      .tickPadding((tickPadding || 10));
    // .tickFormat(this.getIntegerFormat);
  }

  getAwairGlowColorThreshold(sensorType: string): any {
    const scaleList = {
      'score': {
        // [0 <= x < 60], [60 <= x < 80], [80<= x <= 100]
        'domain': [60, 80, 101],
        'range': [/*'#EE4422'*/'#E73740', /*'#D3DA1A'*/'#F9A91B', /*'#6DDA1A'*/'#4FCC53']
      },
      'temp': {
        // x < 13, [13 =< x < 16], [16 =< x < 19], [19 =< x < 22], [22 =< x < 26], [26 =< x < 28], [28 =< x < 30], [30 =< x < 32], 32 =< x
        'domain': [13, 16, 19, 22, 26, 28, 30, 32, Infinity],
        'range': [/*'#EE4422'*/'#E73740', /*'#F29C33'*/'#F78725', /*'#F2B233'*/'#F9A91B', /*'#F2C233'*/'#FCD03D',
          /*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      },
      'humid': {
        // x < 15, [15 =< x < 20], [20 =< x < 35], [35 =< x < 40], [40 =< x < 50], [50 =< x < 60], [60 =< x < 65], [65 =< x < 80], 80 =< x
        'domain': [15, 20, 35, 40, 50, 60, 65, 80, Infinity],
        'range': [/*'#EE4422'*/'#E73740', /*'#F29C33'*/'#F78725', /*'#F2B233'*/'#F9A91B', /*'#F2C233'*/'#FCD03D',
          /*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      },
      'co2': {
        // x < 1000, [1000 <= x < 1500], [1500 <= x < 2000], [2000 <= x < 3000], 3000 =< x
        'domain': [1000, 1500, 2000, 3000, Infinity],
        'range': [/*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      },
      'voc': {
        // x < 345, [345 <= x < 849], [849 <= x < 1200], [1200 <= x < 1487], 1487 =< x
        'domain': [345, 849, 1200, 1487, Infinity],
        'range': [/*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      },
      'dust': {
        // x < 7.49, [7.49 <= x < 22.46], [22.46 <= x < 37.43], [37.43 <= x < 52.4], 52.4 =< x
        'domain': [7.49, 22.46, 37.43, 52.4, Infinity],
        'range': [/*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      },
      'pm25': {
        'domain': [15, 35, 55, 75, Infinity],
        'range': [/*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      }
      // gray '#D2D6DE',
    };

    return d3.scaleThreshold()
      .domain(scaleList[sensorType].domain)
      .range(scaleList[sensorType].range);
  }

  getOmniMintColorThreshold(sensorType: string): ScaleThreshold<number, number> {
    const scaleList = {
      'score': {
        // [0 <= x < 60], [60 <= x < 80], [80<= x <= 100]
        'domain': [1, 60, 80, 101],
        'range': ['#D2D6DE', /*'#EE4422'*/'#E73740', /*'#D3DA1A'*/'#F9A91B', /*'#6DDA1A'*/'#4FCC53']
      },
      'temp': {
        // x < 13, [13 =< x < 16], [16 =< x < 19], [19 =< x < 22], [22 =< x < 26], [26 =< x < 28], [28 =< x < 30], [30 =< x < 32], 32 =< x
        'domain': [13, 16, 19, 22, 26, 28, 30, 32, Infinity],
        'range': [/*'#EE4422'*/'#E73740', /*'#F29C33'*/'#F78725', /*'#F2B233'*/'#F9A91B', /*'#F2C233'*/'#FCD03D',
          /*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      },
      'humid': {
        // x < 15, [15 =< x < 20], [20 =< x < 35], [35 =< x < 40], [40 =< x < 50], [50 =< x < 60], [60 =< x < 65], [65 =< x < 80], 80 =< x
        'domain': [15, 20, 35, 40, 50, 60, 65, 80, Infinity],
        'range': [/*'#EE4422'*/'#E73740', /*'#F29C33'*/'#F78725', /*'#F2B233'*/'#F9A91B', /*'#F2C233'*/'#FCD03D',
          /*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      },
      'co2': {
        // x < 600, [600 <= x < 1000], [1000 <= x < 1500], [1500 <= x < 2500], 2500 =< x
        'domain': [600, 1000, 1500, 2500, Infinity],
        'range': [/*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      },
      'voc': {
        // x < 65, [65 <= x < 200], [200 <= x < 660], [660 <= x < 2200], 2200 =< x
        'domain': [65, 200, 660, 2200, Infinity],
        'range': [/*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      },
      'dust': {
        // x < 7.49, [7.49 <= x < 22.46], [22.46 <= x < 37.43], [37.43 <= x < 52.4], 52.4 =< x
        'domain': [7.49, 22.46, 37.43, 52.4, Infinity],
        'range': [/*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      },
      'pm25': {
        'domain': [15, 35, 55, 75, Infinity],
        'range': [/*'#AFD819'*/'#4FCC53', /*'#F2C233'*/'#FCD03D', /*'#F2B233'*/'#F9A91B', /*'#F29C33'*/'#F78725', /*'#EE4422'*/'#E73740']
      }
      // gray '#D2D6DE',
    };

    return d3.scaleThreshold()
      .domain(scaleList[sensorType].domain)
      .range(scaleList[sensorType].range);
  }

  addLineTransition(line: any): any {
    const datasetLength = line.node().getTotalLength();

    return line
      .attr('stroke-dasharray', datasetLength + ' ' + datasetLength)
      .attr('stroke-dashoffset', datasetLength)
      .transition().duration(600)
      .ease(d3.easeLinear)
      .attr('stroke-dashoffset', 0);
  }

  private getIntegerFormat(datum: any) {
    if (datum % 1 === 0) {
      return d3.format('d')(datum);
    } else {
      return '';
    }
  }

  private getTimeFormat(date): any {
    const formatMillisecond = d3.timeFormat('.%L');
    const formatSecond = d3.timeFormat(':%S');
    const formatMinute = d3.timeFormat('%H:%M');
    const formatHour = d3.timeFormat('%H:00');
    const formatDay = d3.timeFormat('%b %d');
    const formatWeek = d3.timeFormat('%b %d');
    const formatMonth = d3.timeFormat('%b');
    const formatYear = d3.timeFormat('%Y');

    return (d3.timeSecond(date) < date ? formatMillisecond :
      d3.timeMinute(date) < date ? formatSecond :
        d3.timeHour(date) < date ? formatMinute :
          d3.timeDay(date) < date ? formatHour :
            d3.timeMonth(date) < date ? (d3.timeWeek(date) < date ? formatDay : formatWeek) :
              d3.timeYear(date) < date ? formatMonth : formatYear)(date);
  }


}
